import React from "react";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";
import { Loader as CardsPlaceHolder, Button } from "@gonoodle/gn-universe-ui";
import { concatenateQueryParams, invariant } from "@gonoodle/gn-universe-utils";
import { get } from "lodash";
import {
  SECTIONS,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import {
  HeartIcon as HeartIconOutline,
  ClockIcon as ClockIconOutline,
} from "@heroicons/react/outline";

import { useDataLoader } from "../../hooks";
import VideoTile from "../../../VideoTile";
import PrintableTile from "../../../PrintableTile";
import TextTile from "../../../TextTile";
import CustomLinkCardTile from "../../../CustomLinkCardTile";
import BlogCardTile from "../../../BlogCardTile";
import {
  CONTENT_TYPES,
  ROUTE_PREFIX,
  ROUTE_PATHS,
} from "../../../../constants";
import { getSourcePageAndSourcePageTypeFromReferrer } from "../../../../utils/analytics";

const JSURL = require("jsurl");

const PLACEHOLDER_TYPES = {
  PROMPT_ADD_TO_FAVORITES: "promptAddToFavorites",
  PROMPT_START_WATCHING: "promptStartWatching",
  PROMPT_SIGNUP_OR_LOGIN_TO_TRACK_HISTORY: "promptSignUpOrLogInToTrackHistory",
  PROMPT_SIGNUP_OR_LOGIN_TO_SAVE_FAVORITES:
    "promptSignUpOrLogInToSaveFavorites",
};

export default function CardList({ templateOptions, fetchers }) {
  const {
    title,
    displayTitle,
    description,
    placeholder = {},
  } = templateOptions;
  const [fetcher] = fetchers;
  const { data, isLoading } = useDataLoader(fetcher);
  const { contentType, content: cards = [] } = get(data, "[0]", {});
  const numberOfCards = cards.length;
  let gridColumnsClasses = undefined;
  let includeViewFavoritesButton = false;

  if (numberOfCards === 5) {
    gridColumnsClasses = "grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";
  } else {
    gridColumnsClasses = "md:grid-cols-2 lg:grid-cols-4";
  }

  if (isLoading) {
    return (
      <div className="grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4">
        <CardsPlaceHolder cols={4} size="short" mode="dark" />
      </div>
    );
  }

  invariant(
    !(isLoading === false && numberOfCards > 5),
    `CardList: Invalid number of cards expected between 1 and 5 got ${numberOfCards}`,
  );

  if (placeholder.type === PLACEHOLDER_TYPES.PROMPT_ADD_TO_FAVORITES) {
    if (numberOfCards === 0) {
      return (
        <PromptAddToFavorites
          displayTitle={displayTitle}
          description={description}
        />
      );
    }

    includeViewFavoritesButton = true;
  }

  if (
    numberOfCards === 0 &&
    placeholder.type === PLACEHOLDER_TYPES.PROMPT_START_WATCHING
  ) {
    return (
      <PromptStartWatching
        displayTitle={displayTitle}
        description={description}
      />
    );
  }

  if (
    numberOfCards === 0 &&
    placeholder.type ===
      PLACEHOLDER_TYPES.PROMPT_SIGNUP_OR_LOGIN_TO_TRACK_HISTORY
  ) {
    return (
      <PromptSignUpOrLogInToTrackHistory
        displayTitle={displayTitle}
        description={description}
      />
    );
  }

  if (
    numberOfCards === 0 &&
    placeholder.type ===
      PLACEHOLDER_TYPES.PROMPT_SIGNUP_OR_LOGIN_TO_SAVE_FAVORITES
  ) {
    return (
      <PromptSignUpOrLogInToSaveFavorites
        displayTitle={displayTitle}
        description={description}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
            {displayTitle || title}
          </h2>
          <p className="gn-text-sm text-white mb-5">{description}</p>
        </div>
        {includeViewFavoritesButton && <ViewFavoritesButton />}
      </div>
      <ul className={`grid gap-x-md gap-y-md ${gridColumnsClasses}`}>
        {cards.map((card, index) =>
          contentType === CONTENT_TYPES.TAGS ||
          [
            CONTENT_TYPES.TOPIC,
            CONTENT_TYPES.CHANNEL,
            CONTENT_TYPES.TAG,
          ].includes(card.contentType) ? (
            <TextListItem
              key={`${card.id}_${index}`}
              card={card.content ? card.content : card}
              format={card.content ? "landscape" : "square"}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.VIDEOS ||
            card.contentType === CONTENT_TYPES.VIDEO ? (
            <VideoListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.ACTIVITIES ||
            card.contentType === CONTENT_TYPES.ACTIVITY ? (
            <PrintableListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              format={card.content ? "landscape" : "portrait"}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.CUSTOM_LINK ? (
            <LinkCardListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.BLOG_POST ? (
            <BlogPostListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : null,
        )}

        {cards.length < 4 &&
          Array.from({ length: 4 - cards.length }).map((_, index) => (
            <PlaceHolderListItem key={index} placeholder={placeholder} />
          ))}
      </ul>
    </div>
  );
}
function ViewFavoritesButton() {
  const router = useRouter();
  return (
    <Button
      size="sm"
      variant="light-outline"
      className="capitalize space-x-2 whitespace-nowrap"
      onPress={() => router.push(`/${ROUTE_PREFIX.FAVORITES}`)}
    >
      <HeartIconOutline className="h-5" />
      <span>view favorites</span>
    </Button>
  );
}

const getTile = (card, format) => {
  // card.image is used when Card is in a Custom Shelf AND is not a Tag Card
  if (card.image) return card.image;

  // this is used when card is a Tag Card in a Custom Shelf
  if (format === "landscape") {
    if (card.images?.spotlightImage) return card.images.spotlightImage;
    if (card.images?.tileImage) return card.images.tileImage;
  }

  // this is used when card is a content Tag Card in a content Tag Shelf
  if (card.images?.tileImage) return card.images.tileImage;
  if (card.images?.spotlightImage) return card.images.spotlightImage;

  return null;
};

function TextListItem({ card, format, title }) {
  const router = useRouter();
  const tile = getTile(card, format);

  return (
    <li className="flex flex-col justify-top relative">
      <TextTile
        id={card.id}
        slug={card.slug}
        name={card.name}
        title={card.name}
        description={card.description}
        tile={tile}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function VideoListItem({ card, title }) {
  const router = useRouter();
  return (
    <li className="flex flex-col justify-top relative group">
      <VideoTile
        id={card.id}
        slug={card.slug}
        title={card.title}
        description={card.description}
        images={card.images}
        duration={card.duration}
        sponsor={{
          expiration: card.attribution?.expiration,
          type: card.attribution?.type,
        }}
        impressionsProps={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function PrintableListItem({ card, title, format }) {
  const router = useRouter();
  const sources =
    format === "landscape" && !isEmpty(card.heroImage)
      ? {
          "regular@1x": card.heroImage["thumb@1x"],
          "regular@2x": card.heroImage["thumb@2x"],
          "regular@3x": card.heroImage["thumb@3x"],
        }
      : card.image;
  return (
    <li className="flex flex-col justify-top relative group">
      <PrintableTile
        id={card.id}
        slug={card.slug}
        mode="dark"
        sources={sources}
        title={card.title}
        description={card.description}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function LinkCardListItem({ card, title }) {
  const router = useRouter();
  const {
    id,
    slug,
    name,
    description,
    image,
    images,
    title: customLinkTitle,
    destinationUrl,
  } = card;
  return (
    <li className="flex flex-col justify-top relative group">
      <CustomLinkCardTile
        id={id}
        slug={slug}
        sources={images?.spotlightImage || image}
        title={name}
        name={name}
        customLinkTitle={customLinkTitle}
        destinationUrl={destinationUrl}
        description={description}
        format="landscape"
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function BlogPostListItem({ card, title }) {
  const router = useRouter();
  const { slug, title: blogTitle, description, images } = card;

  return (
    <li className="flex flex-col justify-top relative group">
      <BlogCardTile
        slug={slug}
        title={title}
        name={blogTitle}
        description={description}
        imageUrl={images.tile}
        analyticsProps={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function PromptAddToFavorites({ displayTitle, description }) {
  return (
    <div className="flex flex-col space-y-5">
      <div className="flex flex-row justify-between space-x-1">
        <div>
          <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
            {displayTitle}
          </h2>

          <p className="gn-text-sm text-white">{description}</p>
        </div>

        <div className="mt-auto">
          <ViewFavoritesButton />
        </div>
      </div>

      <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
        <HeartIconOutline
          className="w-32 h-32 text-gray-100"
          aria-hidden="true"
        />

        <span className="text-lg text-gray-100">
          You have no favorited videos
        </span>
      </div>
    </div>
  );
}

function PromptStartWatching({ displayTitle, description }) {
  return (
    <div className="flex flex-col space-y-5">
      <div>
        <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
          {displayTitle}
        </h2>

        <p className="gn-text-sm text-white">{description}</p>
      </div>

      <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
        <ClockIconOutline
          className="w-32 h-32 text-gray-100"
          aria-hidden="true"
        />

        <span className="text-lg text-gray-100">Watch some videos</span>
      </div>
    </div>
  );
}

function PromptSignUpOrLogInToTrackHistory({ displayTitle, description }) {
  const router = useRouter();

  return (
    <div className="flex flex-col space-y-5">
      <div>
        <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
          {displayTitle}
        </h2>

        <p className="gn-text-sm text-white">{description}</p>
      </div>

      <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
        <ClockIconOutline
          className="w-32 h-32 text-gray-100"
          aria-hidden="true"
        />

        <span className="text-lg text-gray-100">
          Create a free account to start seeing your recently watched videos
          here!
        </span>

        <Button
          variant="light"
          size="sm"
          className="font-regular lg:whitespace-nowrap"
          onPress={() =>
            router.push(
              concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                referrer: JSURL.stringify({
                  pathname: router.pathname,
                  query: router.query,
                  sourceElement: SECTIONS_TYPES.RECENTLY_WATCHED_SHELF,
                  sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                }),
              }),
              ROUTE_PATHS.REGISTRATION,
            )
          }
        >
          Create a Free Account
        </Button>
      </div>
    </div>
  );
}

function PromptSignUpOrLogInToSaveFavorites({ displayTitle, description }) {
  const router = useRouter();

  return (
    <div className="flex flex-col space-y-5">
      <div>
        <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
          {displayTitle}
        </h2>

        <p className="gn-text-sm text-white">{description}</p>
      </div>

      <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
        <HeartIconOutline
          className="w-28 h-28 text-gray-100"
          aria-hidden="true"
        />

        <span className="text-lg text-gray-100">
          Create a Free Account to start favoriting videos and see them here!
        </span>

        <Button
          variant="light"
          size="sm"
          className="font-regular lg:whitespace-nowrap"
          onPress={() =>
            router.push(
              concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                referrer: JSURL.stringify({
                  pathname: router.pathname,
                  query: router.query,
                  sourceElement: SECTIONS_TYPES.FAVORITES_SHELF,
                  sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                }),
              }),
              ROUTE_PATHS.REGISTRATION,
            )
          }
        >
          Create a Free Account
        </Button>
      </div>
    </div>
  );
}

function PlaceHolderListItem({ placeholder = {} }) {
  const { type } = placeholder;

  if (!type) return null;

  return (
    <li className="flex flex-col justify-top">
      <div className="w-full bg-gray-700 transition-shadow shadow-darkSM aspect-w-16 aspect-h-9">
        {type === PLACEHOLDER_TYPES.PROMPT_START_WATCHING && (
          <div className="flex flex-col justify-center items-center p-1">
            <ClockIconOutline
              className="w-28 h-28 text-gray-100"
              aria-hidden="true"
            />

            <span className="gn-text-sm mt-xs text-gray-100 capitalize">
              Watch More Videos
            </span>
          </div>
        )}

        {type === PLACEHOLDER_TYPES.PROMPT_ADD_TO_FAVORITES && (
          <div className="flex flex-col justify-center items-center p-1">
            <HeartIconOutline
              className="w-28 h-28 text-gray-100"
              aria-hidden="true"
            />

            <span className="gn-text-sm mt-xs text-gray-100 capitalize">
              favorite more videos
            </span>
          </div>
        )}
      </div>
    </li>
  );
}
